import { FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  buildQueryString,
  createResponseContext,
  initialQueryResponse,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../_metronic/helpers'
import { RatcRegistro } from './_models'
import { getRatc } from './_requests'
import { useParametrosRelatorios } from './ListFiltersProvider'
import { useQueryRequest } from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<RatcRegistro>(initialQueryResponse)

/**
 * Página do RATC para o filtro atual. A rota não aceita `search` (RSQL): recebe empresa, período
 * e ceae como parâmetros discretos, então a `query` junta a paginação do estado com os filtros do
 * contexto. Sem filtro completo não há requisição. A volta para a primeira página quando o filtro
 * muda é feita por quem monta os providers (`key` pelo filtro).
 */
export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { state } = useQueryRequest()
  const { periodo } = useParametrosRelatorios()

  // `buildQueryString` descarta valores vazios: sem `ceae`, a rota considera todas as unidades.
  const query = useMemo(
    () => [stringifyRequestQuery(state), buildQueryString(periodo ?? {})].filter(Boolean).join('&'),
    [state, periodo]
  )

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery([QUERIES.INTERCONSULTA_RATC_LIST, query], () => getRatc(query), {
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: !!periodo,
  })

  return (
    <QueryResponseContext.Provider
      value={{
        isLoading: isFetching,
        isError,
        refetch,
        response: periodo ? response : undefined,
        query,
      }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
